import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AiReview, AiReviewType } from '@brafton/feedback-api';

/** How each AI process is named in the chip. */
const AI_REVIEW_TYPE_LABELS: Record<AiReviewType, string> = {
  basic_review: 'Basic review',
  guided_review: 'Guided review',
  advanced_review: 'Advanced review',
  link_check: 'Link check',
  copy_check: 'Spelling & grammar check',
  feedback_audit: 'Feedback audit',
};

/**
 * A small shimmering chip showing the AI processes currently running on a project
 * (e.g. "Link check running", or "2 AI processes running" with the list on hover).
 * Renders nothing when none are running.
 *
 * Purely presentational: the page polls the AI review log and passes the
 * in-progress entries in.
 */
@Component({
  selector: 'app-ai-activity-chip',
  imports: [MatIconModule],
  templateUrl: './ai-activity-chip.html',
  styleUrl: './ai-activity-chip.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiActivityChip {
  /** The project's AI reviews that are in progress. */
  readonly reviews = input<AiReview[]>([]);

  /** The chip's text. */
  protected readonly label = computed(() => {
    const runningReviews = this.reviews();
    return runningReviews.length === 1
      ? `${AI_REVIEW_TYPE_LABELS[runningReviews[0].type] ?? 'AI review'} running`
      : `${runningReviews.length} AI processes running`;
  });

  /** Hover text: every running process, with the page it is on. */
  protected readonly details = computed(() =>
    this.reviews()
      .map((runningReview) => {
        const name = AI_REVIEW_TYPE_LABELS[runningReview.type] ?? 'AI review';
        return runningReview.pageUrl ? `${name}: ${runningReview.pageUrl}` : name;
      })
      .join('\n'),
  );
}
